import { TimeUnits } from '@app/shared/types/enums/time-units.enum';
import { ProffeoUiTimeValue } from '@app/shared/ui/types/time-picker.types';
import { amountAndUnitToDuration } from '@app/shared/utils/time-utils';

/**
 * Pure duration math for the reusable report time-entry prototype. No component state, no I/O.
 */

export function timeValueToMinutes(value: ProffeoUiTimeValue | null | undefined): number {
  if (!value) {
    return 0;
  }
  return value.hours * 60 + value.minutes;
}

/** Formats a time value as a zero-padded `HH:mm` clock string; empty input yields ''. */
export function formatClock(value: ProffeoUiTimeValue | null | undefined): string {
  if (!value) {
    return '';
  }
  const hours = String(value.hours).padStart(2, '0');
  const minutes = String(value.minutes).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Minutes between two clock values on the same day, optionally deducting a break.
 * Returns a value that may be zero or negative so callers can treat it as invalid.
 */
export function rangeDurationMinutes(
  fromTime: ProffeoUiTimeValue | null | undefined,
  toTime: ProffeoUiTimeValue | null | undefined,
  deductBreak: boolean,
  breakMinutes: number
): number {
  const gross = timeValueToMinutes(toTime) - timeValueToMinutes(fromTime);
  const deduction = deductBreak ? Math.max(0, breakMinutes) : 0;
  return gross - deduction;
}

/** Converts positive minutes to an ISO 8601 duration (e.g. 450 -> `PT7H30M`); 0/negative -> `PT0S`. */
export function minutesToIsoDuration(minutes: number): string {
  return amountAndUnitToDuration(minutes, TimeUnits.MINUTES) ?? 'PT0S';
}

/** Total minutes across generated days: `days * durationPerDayMinutes` (0 when either is non-positive). */
export function totalMinutes(days: number, durationPerDayMinutes: number): number {
  if (days <= 0 || durationPerDayMinutes <= 0) {
    return 0;
  }
  return days * durationPerDayMinutes;
}
